import { useRef } from 'react';

const isIOSDevice =
  typeof window !== 'undefined' &&
  (/iP(ad|hone|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

let lockCount = 0;
let savedScrollY = 0;
let iosTouchMoveHandler: ((e: TouchEvent) => void) | null = null;

const isScrollableElement = (el: Element | null): boolean => {
  if (!el || el === document.documentElement || el === document.body) return false;

  const style = getComputedStyle(el);

  if (/scroll|auto/.test(style.overflow + style.overflowY) && el.scrollHeight > el.clientHeight) {
    return true;
  }

  if (style.position === 'fixed') return false;

  return isScrollableElement(el.parentElement);
};

const disableScroll = () => {
  if (lockCount === 0) {
    if (isIOSDevice) {
      savedScrollY = window.scrollY;
      iosTouchMoveHandler = (e: TouchEvent) => {
        if (!isScrollableElement(e.target as Element)) {
          e.preventDefault();
        }
      };
      document.addEventListener('touchmove', iosTouchMoveHandler, { passive: false });
    } else {
      document.body.style.overflow = 'hidden';
    }
  }

  lockCount += 1;
};

const enableScroll = () => {
  if (lockCount === 0) return;

  lockCount -= 1;

  if (lockCount === 0) {
    if (isIOSDevice) {
      if (iosTouchMoveHandler) {
        document.removeEventListener('touchmove', iosTouchMoveHandler);
        iosTouchMoveHandler = null;
      }
      if (window.scrollY !== savedScrollY) {
        window.scrollTo(0, savedScrollY);
      }
    } else {
      document.body.style.overflow = '';
    }
  }
};

const useDisableBodyScroll = () => {
  const previousScrollPosition = useRef(0);

  return {
    disableBodyScroll: (_node: HTMLElement) => {
      disableScroll();
    },
    enableBodyScroll: (_node: HTMLElement) => {
      enableScroll();
    },
    saveScrollPosition: () => {
      previousScrollPosition.current = document.documentElement.scrollTop || document.body.scrollTop;
    },
    applyPreviousScroll: () => {
      document.documentElement.scrollTop = previousScrollPosition.current;
    },
  };
};

export default useDisableBodyScroll;
